import {useEffect, useMemo, useState} from "react";
import {Badge, Box, HStack, Spinner, Text, VStack} from "@chakra-ui/react";
import {patientApiRequest} from "../../lib/api";

function dateLabel(at) {
  if (at === null || at === undefined) return null;
  const date = new Date(at);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, {day: "numeric", month: "short", year: "numeric"});
}

/**
 * Who referred you, and your family doctor — as your clinic has them.
 *
 * Read-only, on purpose. This tab used to list every organisation that refers
 * to the clinic and let a patient add one or edit its details, the fax number
 * included — the number the clinic sends reports to. Who referred somebody is
 * the clinic's record (each referral names who sent it; blhWebServer
 * src/domain/referrals/REFERRALS_DESIGN.md §11), so the patient reads it here,
 * by name and date, through their own route, and tells the clinic when it is
 * wrong.
 */
export default function ReferralHistory({state}) {
  const auth = useMemo(() => ({jwt: state?.jwt || null}), [state?.jwt]);
  const [answer, setAnswer] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    patientApiRequest(auth, {method: "POST", url: "/patient-app/referrers/get", data: {}})
      .then((data) => {
        if (!cancelled) setAnswer(data);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [auth]);

  const referredBy = Array.isArray(answer?.referredBy) ? answer.referredBy : [];

  return (
    <Box w="full" px={0} py={2}>
      <VStack
        spacing={5}
        align="stretch"
        bg="gray.50"
        borderRadius="12px"
        px={{base: 4, md: 6}}
        py={{base: 4, md: 6}}
        mb={4}
      >
        {failed ? (
          <Text fontSize="sm" color="gray.600">
            Your referral information could not be loaded. Please try again later.
          </Text>
        ) : !answer ? (
          <Spinner size="sm" color="gray.400" alignSelf="center" />
        ) : (
          <>
            <Box>
              <Text fontSize="sm" fontWeight="600" color="gray.700" mb={2}>
                Referred by
              </Text>
              {referredBy.length === 0 ? (
                <Text fontSize="sm" color="gray.500">
                  Your clinic has no referral recorded for you.
                </Text>
              ) : (
                <VStack spacing={2} align="stretch">
                  {referredBy.map((entry, index) => (
                    <Box
                      key={`${entry.name}-${entry.at ?? "chart"}-${index}`}
                      bg="white"
                      borderWidth="1px"
                      borderColor="gray.200"
                      borderRadius="8px"
                      px={3}
                      py={2}
                    >
                      <HStack spacing={2}>
                        <Text fontSize="sm" fontWeight="500" color="gray.800">
                          {entry.name}
                        </Text>
                        {entry.current && (
                          <Badge colorScheme="blue" variant="subtle" fontSize="10px">
                            Current
                          </Badge>
                        )}
                      </HStack>
                      <Text fontSize="xs" color="gray.500">
                        {dateLabel(entry.at) ? `Referral received ${dateLabel(entry.at)}` : "On file"}
                      </Text>
                    </Box>
                  ))}
                </VStack>
              )}
            </Box>

            <Box>
              <Text fontSize="sm" fontWeight="600" color="gray.700" mb={1}>
                Family doctor
              </Text>
              <Text fontSize="sm" color={answer.familyDoctor ? "gray.800" : "gray.500"}>
                {answer.familyDoctor?.name || "Not recorded"}
              </Text>
            </Box>

            <Text fontSize="xs" color="gray.500">
              Your clinic keeps this information. If something here is not right, let them know.
            </Text>
          </>
        )}
      </VStack>
    </Box>
  );
}
